import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── LOGO URLS (Vite-resolved at build time) ──────────────────────────────────
const nishSrc  = new URL('../../assets/images/NISHlogo.png',  import.meta.url).href;
const ncahtSrc = new URL('../../assets/images/NCAHTlogo.png', import.meta.url).href;
const icmrSrc  = new URL('../../assets/images/ICMRlogo.png',  import.meta.url).href;

async function imgToBase64(src) {
  try {
    const res  = await fetch(src);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const WORKSHEET_TYPES = [
  { id:"odd-one-out",     label:"Odd One Out",           icon:"🔍", color:"#FF6B35", bg:"#FFF3EE", desc:"4 images — one doesn't belong" },
  { id:"match-following", label:"Match the Following",   icon:"🔗", color:"#2E86AB", bg:"#EEF6FB", desc:"Draw lines to match pairs" },
  { id:"sequence-order",  label:"Sequence & Ordering",   icon:"🔢", color:"#7B2D8B", bg:"#F5EEF8", desc:"Arrange images in correct order" },
  { id:"count-items",     label:"Count the Items",       icon:"🔢", color:"#1B8A5A", bg:"#EDF7F2", desc:"Count objects and write the number" },
  { id:"find-differences",label:"Find the Differences",  icon:"👀", color:"#C0392B", bg:"#FDEDEC", desc:"Spot differences between two images" },
];

const LANGUAGES = [
  { code:"en", name:"English"  },
  { code:"ml", name:"മലയാളം" },
  { code:"hi", name:"हिंदी"   },
  { code:"ta", name:"தமிழ்"  },
  { code:"te", name:"తెలుగు" },
];

const INSTRUCTIONS = {
  "odd-one-out":     { en:"Circle the picture that does NOT belong with the others.", ml:"മറ്റുള്ളവയോട് ചേരാത്ത ചിത്രത്തിന് വൃത്തം വരയ്ക്കൂ.", hi:"उस चित्र पर गोला बनाएं जो दूसरों से अलग है।", ta:"மற்றவற்றுடன் சேராத படத்தை வட்டமிடுங்கள்.", te:"ఇతరులతో సరిపోని చిత్రానికి వృత్తం గీయండి." },
  "match-following": { en:"Draw a line to match the pictures on the left with the pictures on the right.", ml:"ഇടത്തെ ചിത്രങ്ങൾ വലത്തെ ചിത്രങ്ങളുമായി ബന്ധിപ്പിക്കൂ.", hi:"बाईं ओर के चित्रों को दाईं ओर के चित्रों से मिलाएं।", ta:"இடது படங்களை வலது படங்களுடன் இணைக்கவும்.", te:"ఎడమ వైపు చిత్రాలను కుడి వైపు చిత్రాలతో కలపండి." },
  "sequence-order":  { en:"Number the pictures 1, 2, 3, 4 to show the correct order.", ml:"ചിത്രങ്ങൾ 1, 2, 3, 4 എന്ന ക്രമത്തിൽ നമ്പർ ഇടൂ.", hi:"चित्रों को सही क्रम में 1, 2, 3, 4 नंबर दें।", ta:"படங்களை சரியான வரிசையில் 1, 2, 3, 4 எண்ணிடுங்கள்.", te:"చిత్రాలను 1, 2, 3, 4 సరైన క్రమంలో నంబర్ ఇవ్వండి." },
  "count-items":     { en:"Count the pictures in each box and write the number.", ml:"ഓരോ ബോക്സിലും ഉള്ള ചിത്രങ്ങൾ എണ്ണി സംഖ്യ എഴുതൂ.", hi:"प्रत्येक बॉक्स में चित्र गिनें और संख्या लिखें।", ta:"ஒவ்வொரு பெட்டியிலும் உள்ள படங்களை எண்ணி எழுதுங்கள்.", te:"ప్రతి బాక్స్‌లోని చిత్రాలను లెక్కించి సంఖ్య రాయండి." },
  "find-differences":{ en:"Look at both pictures carefully. Circle the differences you can find.", ml:"രണ്ട് ചിത്രങ്ങളും ശ്രദ്ധയോടെ നോക്കൂ. വ്യത്യാസങ്ങൾ കണ്ടെത്തി വൃത്തം ഇടൂ.", hi:"दोनों चित्रों को ध्यान से देखें। अंतर ढूंढकर गोला बनाएं।", ta:"இரண்டு படங்களையும் கவனமாக பாருங்கள். வேறுபாடுகளை வட்டமிடுங்கள்.", te:"రెండు చిత్రాలను జాగ్రత్తగా చూడండి. తేడాలు కనుగొని వృత్తం గీయండి." },
};

// ─── URL RESOLVER ─────────────────────────────────────────────────────────────
// Handles all image URL shapes that come from the collection API or uploads
function resolveUrl(item) {
  if (!item) return "";
  const base = API.replace("/api", "");

  // Already a full URL or data URI — use as-is
  if (item.imageUrl?.startsWith("http") || item.imageUrl?.startsWith("data:")) return item.imageUrl;

  // Server-relative path like /uploads/...
  if (item.imageUrl?.startsWith("/")) return `${base}${item.imageUrl}`;

  // url field (some collection docs use this)
  if (item.url?.startsWith("http") || item.url?.startsWith("data:")) return item.url;
  if (item.url?.startsWith("/")) return `${base}${item.url}`;

  return "";
}

// ─── A4 PDF ENGINE ────────────────────────────────────────────────────────────
const A4W = 794, A4H = 1123, PAD = 28;

function pageShell(title, instruction, color, langName, pageNum, totalPages, bodyHtml, logos = {}) {
  const hh = 60, fh = 38, ih = 36;
  const LOGO_H = 36;
  const nishImg  = logos.nish  ? `<img src="${logos.nish}"  style="height:${LOGO_H}px;width:auto;object-fit:contain;display:block;" alt="NISH"/>` : "";
  const ncahtImg = logos.ncaht ? `<img src="${logos.ncaht}" style="height:28px;width:auto;object-fit:contain;display:block;" alt="NCAHT"/>` : "";
  const icmrImg  = logos.icmr  ? `<img src="${logos.icmr}"  style="height:28px;width:auto;object-fit:contain;display:block;" alt="ICMR"/>` : "";
  return `
  <div style="width:${A4W}px;height:${A4H}px;padding:${PAD}px;box-sizing:border-box;background:white;display:flex;flex-direction:column;page-break-after:always;overflow:hidden;font-family:'Segoe UI',Arial,sans-serif;">
    <div style="height:${hh}px;display:flex;align-items:center;justify-content:space-between;border-bottom:2.5px solid ${color};padding-bottom:8px;margin-bottom:10px;flex-shrink:0;">
      <div style="display:flex;align-items:center;gap:10px;">
        ${nishImg}
        <div style="font-size:7.5px;color:#555;line-height:1.5;">
          <div style="font-weight:700;color:#003366;font-size:8.5px;">National Institute of Speech &amp; Hearing</div>
          <div>Thiruvananthapuram, Kerala · Foundational Stage Learning Platform</div>
        </div>
      </div>
      <div style="font-weight:800;font-size:14px;color:${color};">${title}</div>
      <div style="display:flex;align-items:center;gap:8px;">${ncahtImg}${icmrImg}</div>
    </div>
    <div style="height:${ih}px;background:${color}18;border-left:4px solid ${color};padding:7px 12px;border-radius:0 6px 6px 0;margin-bottom:10px;font-size:10.5px;color:#222;font-weight:600;flex-shrink:0;display:flex;align-items:center;">${instruction}</div>
    <div style="flex:1;min-height:0;display:flex;flex-direction:column;">${bodyHtml}</div>
    <div style="font-size:7px;color:#ccc;text-align:center;flex-shrink:0;margin:3px 0 2px;">Page ${pageNum} of ${totalPages}</div>
    <div style="height:50px;border-top:1.5px solid ${color};padding-top:8px;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;">
      <div style="display:flex;align-items:center;gap:8px;">
        ${logos.nish  ? `<img src="${logos.nish}"  style="height:26px;width:auto;object-fit:contain;" alt="NISH"/>` : `<span style="font-size:7px;color:#888;">NISH</span>`}
        ${logos.ncaht ? `<img src="${logos.ncaht}" style="height:20px;width:auto;object-fit:contain;" alt="NCAHT"/>` : `<span style="font-size:7px;color:#888;">NCAHT</span>`}
        ${logos.icmr  ? `<img src="${logos.icmr}"  style="height:20px;width:auto;object-fit:contain;" alt="ICMR"/>` : `<span style="font-size:7px;color:#888;">ICMR</span>`}
      </div>
      <div style="display:flex;gap:14px;align-items:center;">
        <span style="font-size:7.5px;color:#888;">Name: <span style="display:inline-block;width:130px;border-bottom:1px solid #999;"></span></span>
        <span style="font-size:7.5px;color:#888;">Date: <span style="display:inline-block;width:70px;border-bottom:1px solid #999;"></span></span>
        <span style="font-size:7.5px;color:#888;">Score: <span style="display:inline-block;width:40px;border-bottom:1px solid #999;"></span></span>
      </div>
      <span style="font-size:7.5px;color:#888;">Lang: ${langName}</span>
    </div>
  </div>`;
}

function imgCell(item, color, size=110, showLabel=true, badge=null) {
  const url = resolveUrl(item);
  const label = item.labels?.en || item.name || "";
  return `
    <div style="border:2px solid ${color};border-radius:10px;padding:8px;text-align:center;background:white;position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;">
      ${badge!==null?`<div style="position:absolute;top:5px;right:5px;width:22px;height:22px;border:2px solid ${color};border-radius:50%;background:white;display:flex;align-items:center;justify-content:center;font-size:9px;color:${color};font-weight:700;">${badge}</div>`:""}
      ${url?`<img src="${url}" style="width:${size}px;height:${size}px;object-fit:contain;" crossorigin="anonymous"/>`:`<div style="width:${size}px;height:${size}px;background:#f0f4f8;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:11px;color:#aaa;">No image</div>`}
      ${showLabel?`<div style="margin-top:5px;font-size:10px;font-weight:700;color:#333;">${label}</div>`:""}
    </div>`;
}

function renderOddOneOut(groups, instruction, language, title, logos = {}) {
  const color = "#FF6B35";
  const langName = LANGUAGES.find(l=>l.code===language)?.name||language;
  const pages = groups.map((g, gi) => {
    const items = g.items.slice(0,6);
    const cellSize = 100;
    const cells = items.map((item,i) => imgCell(item,color,cellSize,true,i+1)).join("");
    const body = `
      <div style="background:#FFF3EE;border-radius:10px;padding:14px;flex:1;display:flex;flex-direction:column;gap:12px;">
        <div style="font-size:12px;font-weight:800;color:${color};">Group ${gi+1}: ${g.label||""}</div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;flex:1;">${cells}</div>
        <div style="padding:10px 12px;background:white;border:1.5px dashed ${color};border-radius:8px;font-size:10.5px;color:#555;">
          The odd one out is number: <span style="display:inline-block;width:32px;border-bottom:1.5px solid #333;margin:0 4px;"></span>
          because: <span style="display:inline-block;width:220px;border-bottom:1.5px solid #333;margin:0 4px;"></span>
        </div>
      </div>`;
    return pageShell(title, instruction, color, langName, gi+1, groups.length, body, logos);
  });
  return pages.join("");
}

function renderMatchFollowing(items, instruction, language, title, logos = {}) {
  const color = "#2E86AB";
  const langName = LANGUAGES.find(l=>l.code===language)?.name||language;
  const half = Math.ceil(items.length/2);
  const left  = items.slice(0, half);
  const right = [...items.slice(half)].sort(() => Math.random()-0.5);
  while (right.length < left.length) right.push(null);
  const rows = left.length;
  const bodyH  = A4H - PAD*2 - 60 - 10 - 36 - 10 - 38 - 5;
  const rowH   = Math.floor(bodyH / rows);
  const cardH  = rowH - 10;
  const imgSize = Math.max(Math.min(cardH - 28, 130), 60);
  const cardW  = imgSize + 20;
  let tableRows = "";
  for (let i = 0; i < rows; i++) {
    const l = left[i], r = right[i];
    const card = (item, borderColor) => item ? `
      <div style="border:2px solid ${borderColor};border-radius:10px;padding:8px;background:white;display:inline-flex;flex-direction:column;align-items:center;gap:4px;width:${cardW}px;box-sizing:border-box;">
        ${resolveUrl(item)?`<img src="${resolveUrl(item)}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;" crossorigin="anonymous"/>`:`<div style="width:${imgSize}px;height:${imgSize}px;background:#f0f4f8;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:10px;color:#aaa;">No image</div>`}
        <div style="font-size:9.5px;font-weight:700;color:#333;text-align:center;max-width:${cardW-16}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${item.labels?.en||item.name}</div>
      </div>` : "";
    tableRows += `
      <tr style="height:${rowH}px;">
        <td style="text-align:center;vertical-align:middle;width:${cardW+16}px;">${card(l, color)}</td>
        <td style="text-align:center;vertical-align:middle;width:70px;"><div style="width:44px;height:44px;border:1.5px dashed #bbb;border-radius:50%;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:16px;color:#ddd;">✦</div></td>
        <td style="vertical-align:middle;width:80px;"></td>
        <td style="text-align:center;vertical-align:middle;width:${cardW+16}px;">${card(r, "#ccc")}</td>
      </tr>`;
  }
  const body = `
    <div style="flex:1;display:flex;flex-direction:column;">
      <div style="display:flex;font-size:10.5px;font-weight:700;color:${color};margin-bottom:4px;">
        <span style="width:${cardW+16}px;text-align:center;">Column A</span>
        <span style="width:70px;"></span>
        <span style="width:80px;"></span>
        <span style="width:${cardW+16}px;text-align:center;">Column B</span>
      </div>
      <table style="width:100%;border-collapse:collapse;">${tableRows}</table>
    </div>`;
  return pageShell(title, instruction, color, langName, 1, 1, body, logos);
}

function renderSequenceOrder(items, instruction, language, title, logos = {}) {
  const color = "#7B2D8B";
  const langName = LANGUAGES.find(l=>l.code===language)?.name||language;
  const shuffled = [...items].sort(()=>Math.random()-0.5);
  const cols = Math.min(shuffled.length, 4);
  const imgSize = Math.min(Math.floor((A4W-PAD*2-cols*20)/cols)-16, 120);
  const cells = shuffled.map((item) => `
    <div style="border:2px solid ${color};border-radius:10px;padding:10px;text-align:center;background:white;display:flex;flex-direction:column;align-items:center;gap:6px;">
      ${resolveUrl(item)?`<img src="${resolveUrl(item)}" style="width:${imgSize}px;height:${imgSize}px;object-fit:contain;" crossorigin="anonymous"/>`:`<div style="width:${imgSize}px;height:${imgSize}px;background:#f0f4f8;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:10px;color:#aaa;">No image</div>`}
      <div style="font-size:9.5px;font-weight:700;color:#333;">${item.labels?.en||item.name}</div>
      <div style="border-top:1px dashed #ccc;width:100%;padding-top:6px;font-size:9.5px;color:#888;">Order: <span style="display:inline-block;width:28px;height:22px;border:1.5px solid ${color};border-radius:4px;vertical-align:middle;"></span></div>
    </div>`).join("");
  const stepLines = shuffled.map((_,i) => `<span style="font-size:9.5px;">Step ${i+1}: <span style="display:inline-block;width:90px;border-bottom:1.5px solid #999;"></span></span>`).join("");
  const body = `
    <div style="flex:1;display:flex;flex-direction:column;gap:14px;justify-content:center;">
      <div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:12px;">${cells}</div>
      <div style="background:#F5EEF8;border-radius:8px;padding:12px;border:1.5px dashed ${color};">
        <div style="font-size:10px;font-weight:700;color:${color};margin-bottom:8px;">Write the correct order below:</div>
        <div style="display:flex;gap:14px;flex-wrap:wrap;">${stepLines}</div>
      </div>
    </div>`;
  return pageShell(title, instruction, color, langName, 1, 1, body, logos);
}

function renderCountItems(items, instruction, language, title, logos = {}) {
  const color = "#1B8A5A";
  const langName = LANGUAGES.find(l=>l.code===language)?.name||language;
  const boxes = items.slice(0,6).map(item => {
    const count = typeof item.count === "number" && item.count > 0 ? item.count : 1;
    const imgs = Array.from({length:count}).map(()=>
      resolveUrl(item)
        ? `<img src="${resolveUrl(item)}" style="width:38px;height:38px;object-fit:contain;margin:2px;" crossorigin="anonymous"/>`
        : `<div style="width:38px;height:38px;background:#e0e7f0;border-radius:4px;margin:2px;display:inline-flex;align-items:center;justify-content:center;font-size:9px;color:#aaa;">?</div>`
    ).join("");
    return `
      <div style="border:2px solid ${color};border-radius:10px;overflow:hidden;background:white;display:flex;flex-direction:column;">
        <div style="background:${color}18;padding:10px;flex:1;display:flex;flex-wrap:wrap;align-items:center;justify-content:center;min-height:110px;">${imgs}</div>
        <div style="padding:7px 10px;display:flex;align-items:center;justify-content:space-between;border-top:1.5px dashed ${color};">
          <span style="font-size:9.5px;font-weight:700;color:#555;">${item.labels?.en||item.name}</span>
          <span style="font-size:9.5px;color:#888;">Count: <span style="display:inline-block;width:32px;height:20px;border:1.5px solid ${color};border-radius:4px;"></span></span>
        </div>
      </div>`;
  }).join("");
  const body = `<div style="flex:1;display:grid;grid-template-columns:repeat(3,1fr);gap:14px;">${boxes}</div>`;
  return pageShell(title, instruction, color, langName, 1, 1, body, logos);
}

function renderFindDifferences(item, instruction, language, title, logos = {}) {
  const color = "#C0392B";
  const langName = LANGUAGES.find(l=>l.code===language)?.name||language;
  const urlA = resolveUrl(item);
  const urlB = (() => {
    if (!item?.imageUrlB) return urlA;
    const base = API.replace("/api","");
    return item.imageUrlB.startsWith("/uploads") ? `${base}${item.imageUrlB}` : item.imageUrlB;
  })();
  const diffCount = item?.differenceCount ?? 5;
  const bodyH = A4H - PAD*2 - 60-10-36-10-38-5;
  const imgH = Math.floor((bodyH - 110)/1);
  const body = `
    <div style="flex:1;display:flex;flex-direction:column;gap:12px;">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;flex:1;">
        ${[["Picture A", urlA],["Picture B", urlB]].map(([label, url])=>`
          <div style="border:2px solid ${color};border-radius:10px;overflow:hidden;display:flex;flex-direction:column;">
            <div style="background:${color}15;padding:7px;text-align:center;font-size:10px;font-weight:700;color:${color};border-bottom:1px solid ${color}40;">${label}</div>
            <div style="flex:1;display:flex;align-items:center;justify-content:center;background:#fafafa;padding:12px;">
              ${url?`<img src="${url}" style="max-width:100%;max-height:${imgH}px;object-fit:contain;" crossorigin="anonymous"/>`:`<div style="color:#aaa;font-size:12px;">No image</div>`}
            </div>
          </div>`).join("")}
      </div>
      <div style="background:#FDEDEC;border-radius:8px;padding:12px;border:1.5px dashed ${color};flex-shrink:0;">
        <div style="font-size:10px;font-weight:700;color:${color};margin-bottom:8px;">Circle the differences on the pictures above, then describe them below:</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
          ${Array.from({length:diffCount}).map((_,n)=>`<div style="font-size:9.5px;">Difference ${n+1}: <span style="display:inline-block;width:170px;border-bottom:1px solid #999;"></span></div>`).join("")}
        </div>
      </div>
    </div>`;
  return pageShell(title, instruction, color, langName, 1, 1, body, logos);
}

async function buildAndPrint(type, items, instruction, language, title) {
  if (!items.length) {
    alert("Please select at least one image before generating.");
    return;
  }
  const instr = instruction || INSTRUCTIONS[type]?.[language] || "";
  const t = title || WORKSHEET_TYPES.find(w=>w.id===type)?.label || "Worksheet";
  const [nishB64, ncahtB64, icmrB64] = await Promise.all([
    imgToBase64(nishSrc), imgToBase64(ncahtSrc), imgToBase64(icmrSrc),
  ]);
  const logos = { nish: nishB64, ncaht: ncahtB64, icmr: icmrB64 };
  let html = "";
  if (type === "odd-one-out") {
    const groups = [];
    for (let i=0; i<items.length; i+=6) groups.push({ label:`Group ${groups.length+1}`, items:items.slice(i,i+6) });
    html = renderOddOneOut(groups, instr, language, t, logos);
  } else if (type === "match-following") {
    html = renderMatchFollowing(items, instr, language, t, logos);
  } else if (type === "sequence-order") {
    html = renderSequenceOrder(items.slice(0,6), instr, language, t, logos);
  } else if (type === "count-items") {
    html = renderCountItems(items, instr, language, t, logos);
  } else if (type === "find-differences") {
    html = renderFindDifferences(items[0], instr, language, t, logos);
  }
  const win = window.open("","_blank");
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{background:#888;}
    @media print{body{background:white;}@page{size:A4 portrait;margin:0;}div{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
  </style></head><body>${html}</body></html>`);
  win.document.close();
  setTimeout(()=>win.print(), 800);
}

// ─── IndicatorTagPicker ───────────────────────────────────────────────────────
// Shared chip-picker for the 40 NCF indicators, grouped by domain. Self-contained
// copy of the same component used in CollectionManager / FlashcardGenerator —
// kept local here so this file stays drop-in deployable. (Candidate for
// extraction to a shared component file later.)
function IndicatorTagPicker({ taxonomy, selected, onToggle, compact }) {
  const [openDomain, setOpenDomain] = useState(null);
  if (!taxonomy) return (
    <div style={{ fontSize:11, color:"#aaa" }}>Loading indicator list…</div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {taxonomy.domains.map(dom => {
        const inds = taxonomy.indicators.filter(i => i.domainId === dom.id);
        const selCount = inds.filter(i => selected.includes(i.id)).length;
        const open = openDomain === dom.id;
        return (
          <div key={dom.id} style={{ border:"1.5px solid #e0e7f0", borderRadius:9, overflow:"hidden" }}>
            <button onClick={() => setOpenDomain(open ? null : dom.id)}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding: compact ? "6px 10px" : "8px 12px",
                       background: selCount > 0 ? "#EBF4FB" : "white", border:"none", cursor:"pointer", textAlign:"left" }}>
              <span style={{ fontSize: compact ? 13 : 15 }}>{dom.icon}</span>
              <span style={{ flex:1, fontSize: compact ? 11 : 12, fontWeight:700, color:"#003366" }}>{dom.label}</span>
              {selCount > 0 && (
                <span style={{ fontSize:10, fontWeight:800, color:"#003366", background:"white",
                               border:"1px solid #BDD7EE", borderRadius:10, padding:"1px 7px" }}>{selCount}</span>
              )}
              <span style={{ fontSize:11, color:"#888" }}>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, padding:"10px 12px", background:"#FAFCFF" }}>
                {inds.map(ind => {
                  const on = selected.includes(ind.id);
                  return (
                    <button key={ind.id} onClick={() => onToggle(ind.id)}
                      title={ind.label}
                      style={{ padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:700,
                               border:`1.5px solid ${on ? "#003366" : "#e0e7f0"}`,
                               background: on ? "#003366" : "white",
                               color: on ? "white" : "#555", cursor:"pointer" }}>
                      {ind.id} {on ? "✓" : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── COLLECTION PICKER MODAL ──────────────────────────────────────────────────
// FIX: Collection API returns docs with both .imageUrl and .url fields.
// We try imageUrl first (direct CDN/S3), then fall back to .url (server-relative).
// This matches the same logic in FlashcardGenerator's CollectionPickerModal.
function CollectionPickerModal({ onPick, onClose, getAuthHeader, multiSelect=true }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [picked,  setPicked]  = useState(new Set());
  const [error,   setError]   = useState("");

  useEffect(() => {
    (async () => {
      try {
        const base = API.replace("/api", "");
        const res  = await fetch(`${API}/collection?limit=500`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) {
          setItems(data.images.map(doc => {
            // Resolve image URL — same logic as FlashcardGenerator
            let imageUrl = "";
            if (doc.imageUrl?.startsWith("http") || doc.imageUrl?.startsWith("data:")) {
              imageUrl = doc.imageUrl;
            } else if (doc.imageUrl?.startsWith("/")) {
              imageUrl = `${base}${doc.imageUrl}`;
            } else if (doc.url?.startsWith("http") || doc.url?.startsWith("data:")) {
              imageUrl = doc.url;
            } else if (doc.url?.startsWith("/")) {
              imageUrl = `${base}${doc.url}`;
            }

            const fallback = doc.title
              || doc.originalName?.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ")
              || "Image";

            return {
              id:       doc._id,
              name:     doc.labels?.en?.trim() || fallback,
              imageUrl,
              labels:   {
                en: doc.labels?.en?.trim() || fallback,
                ml: doc.labels?.ml?.trim() || "",
                hi: doc.labels?.hi?.trim() || "",
                ta: doc.labels?.ta?.trim() || "",
                te: doc.labels?.te?.trim() || "",
              },
              category: doc.category || "Custom",
              indicators: doc.indicators || [],
            };
          }));
        } else {
          setError("Failed to load collection.");
        }
      } catch (e) {
        setError("Could not reach server.");
      }
      setLoading(false);
    })();
  }, []);

  const filtered = items.filter(it =>
    !search ||
    it.name.toLowerCase().includes(search.toLowerCase()) ||
    Object.values(it.labels || {}).some(v => v.toLowerCase().includes(search.toLowerCase()))
  );

  const toggle = id => {
    if (!multiSelect) { setPicked(new Set([id])); return; }
    setPicked(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",display:"flex",
                 alignItems:"center",justifyContent:"center",zIndex:300,padding:16}}
      onClick={onClose}>
      <div style={{background:"#F4F7FB",borderRadius:18,width:820,maxWidth:"96vw",
                   maxHeight:"92vh",display:"flex",flexDirection:"column",
                   boxShadow:"0 32px 80px rgba(0,0,0,.35)"}}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:"16px 24px 12px",borderBottom:"1px solid #E0E7F0",
                     display:"flex",alignItems:"center",gap:12,
                     background:"white",borderRadius:"18px 18px 0 0"}}>
          <div style={{fontWeight:800,fontSize:15,color:"#003366",flex:1}}>🗂️ Browse Collection</div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search images…"
            style={{border:"1.5px solid #DCE3F5",borderRadius:9,padding:"7px 13px",
                    fontSize:12,outline:"none",width:220,background:"#F8FAFF"}}/>
          <span style={{fontSize:12,color:"#888",fontWeight:600,background:"#F0F4FF",
                        padding:"3px 10px",borderRadius:8}}>
            {picked.size} selected
          </span>
          <button onClick={onClose}
            style={{background:"#F0F4FF",border:"none",borderRadius:8,
                    padding:"6px 13px",cursor:"pointer",color:"#666",fontSize:13}}>✕</button>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          {loading ? (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                         justifyContent:"center",padding:60,gap:14}}>
              <div style={{width:32,height:32,border:"3px solid #DCE3F5",
                           borderTop:"3px solid #003366",borderRadius:"50%",
                           animation:"ws-spin .7s linear infinite"}}/>
              <div style={{fontSize:13,color:"#AAA"}}>Loading collection…</div>
              <style>{`@keyframes ws-spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          ) : error ? (
            <div style={{textAlign:"center",padding:60,color:"#C0392B",fontSize:13,fontWeight:600}}>
              ⚠️ {error}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{textAlign:"center",padding:60,color:"#AAA"}}>
              <div style={{fontSize:36,marginBottom:10}}>🖼️</div>
              <div style={{fontSize:13,fontWeight:600}}>
                {search ? "No images match your search" : "No images in collection yet"}
              </div>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10}}>
              {filtered.map(item => {
                const on = picked.has(item.id);
                return (
                  <div key={item.id} onClick={() => toggle(item.id)}
                    style={{border:`2.5px solid ${on ? "#003366" : "#E0E7F0"}`,
                            borderRadius:11,overflow:"hidden",cursor:"pointer",
                            background:"white",position:"relative",
                            boxShadow:on?"0 0 0 3px rgba(0,51,102,.12)":"none",
                            transition:"all .12s"}}>
                    {on && (
                      <div style={{position:"absolute",top:6,right:6,background:"#003366",
                                   color:"white",borderRadius:"50%",width:20,height:20,
                                   display:"flex",alignItems:"center",justifyContent:"center",
                                   fontSize:11,fontWeight:900,zIndex:2}}>✓</div>
                    )}
                    {item.indicators?.length > 0 && (
                      <div style={{position:"absolute",top:6,left:6,background:"rgba(0,51,102,.85)",
                                   color:"white",fontSize:9,fontWeight:800,padding:"1px 6px",
                                   borderRadius:6,zIndex:2}}
                        title={item.indicators.join(", ")}>
                        🎯 {item.indicators.length}
                      </div>
                    )}
                    <div style={{height:88,display:"flex",alignItems:"center",
                                 justifyContent:"center",background:"#F8F9FC",padding:8}}>
                      {item.imageUrl ? (
                        <img src={item.imageUrl} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}
                          alt={item.name}
                          onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                        />
                      ) : null}
                      <div style={{display: item.imageUrl ? "none" : "flex",
                                   width:"100%",height:"100%",alignItems:"center",
                                   justifyContent:"center",color:"#CCC",fontSize:11}}>
                        No image
                      </div>
                    </div>
                    <div style={{padding:"5px 7px",fontSize:10,fontWeight:600,color:"#333",
                                 overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                 borderTop:"1px solid #F0F4FF"}}>
                      {item.name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"12px 24px",borderTop:"1px solid #E0E7F0",background:"white",
                     borderRadius:"0 0 18px 18px",display:"flex",
                     justifyContent:"space-between",alignItems:"center",gap:10}}>
          <div style={{fontSize:12,color:"#888"}}>
            {filtered.length} image{filtered.length !== 1 ? "s" : ""} in collection
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose}
              style={{padding:"8px 18px",background:"white",color:"#666",
                      border:"1.5px solid #DCE3F5",borderRadius:8,fontWeight:600,
                      fontSize:13,cursor:"pointer"}}>Cancel</button>
            <button onClick={() => onPick(items.filter(i => picked.has(i.id)))}
              disabled={picked.size === 0}
              style={{padding:"8px 22px",
                      background:picked.size > 0 ? "#003366" : "#CCC",
                      color:"white",border:"none",borderRadius:8,
                      fontWeight:700,fontSize:13,
                      cursor:picked.size > 0 ? "pointer" : "not-allowed"}}>
              Add {picked.size > 0 ? `${picked.size} Image${picked.size > 1 ? "s" : ""}` : "Images"} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SAVED WORKSHEETS INLINE SIDEBAR ─────────────────────────────────────────
// Lives permanently on the right side of the screen — not a modal.
// Refreshes whenever `refreshKey` changes (pass Date.now() after save).
function SavedWorksheetsInline({ getAuthHeader, onLoad, onPrint, refreshKey }) {
  const [worksheets, setWorksheets] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [loadErr,    setLoadErr]    = useState("");
  const [filterType, setFilterType] = useState("all");
  const [search,     setSearch]     = useState("");
  const [deleting,   setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const q = filterType !== "all" ? `&type=${filterType}` : "";
      const s = search ? `&search=${encodeURIComponent(search)}` : "";
      const res  = await fetch(`${API}/worksheet?limit=50${q}${s}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const t = await res.text();
        setLoadErr(`Server ${res.status}: ${t.slice(0, 120)}`);
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setWorksheets(data.worksheets || []);
      } else {
        setLoadErr(data.message || "Load failed");
      }
    } catch (e) {
      setLoadErr(e.message || "Network error");
    }
    setLoading(false);
  }, [filterType, search, getAuthHeader]);

  // Reload on filter/search change AND whenever a save happens (refreshKey bumped)
  useEffect(() => { load(); }, [load, refreshKey]);

  const deleteWs = async (id, e) => {
    e.stopPropagation();
    setDeleting(id);
    try {
      await fetch(`${API}/worksheet/${id}`, { method:"DELETE", headers:getAuthHeader() });
      setWorksheets(p => p.filter(w => w._id !== id));
    } catch {}
    setDeleting(null);
  };

  const relDate = (iso) => {
    if (!iso) return "";
    const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
    if (d === 0) return "Today";
    if (d === 1) return "Yesterday";
    if (d < 7)   return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day:"numeric", month:"short" });
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <style>{`@keyframes ws-spin{to{transform:rotate(360deg)}}`}</style>

      {/* Panel header */}
      <div style={{background:"linear-gradient(135deg,#003366 0%,#1A4A6E 100%)",
                   padding:"16px 18px",borderRadius:"12px 12px 0 0",flexShrink:0}}>
        <div style={{fontFamily:"'Playfair Display',Georgia,serif",
                     fontSize:16,fontWeight:900,color:"white",marginBottom:2}}>
          📂 Saved Worksheets
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,.6)"}}>
          {loading ? "Loading…" : `${worksheets.length} saved`}
        </div>
      </div>

      {/* Search */}
      <div style={{padding:"10px 12px",background:"white",borderBottom:"1px solid #E4ECF5",
                   flexShrink:0}}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search…"
          style={{width:"100%",border:"1.5px solid #DCE3F5",borderRadius:8,
                  padding:"7px 10px",fontSize:11,outline:"none",
                  background:"#F8FAFF",boxSizing:"border-box"}}/>
      </div>

      {/* Type filter pills */}
      <div style={{padding:"8px 12px",display:"flex",gap:5,flexWrap:"wrap",
                   background:"white",borderBottom:"1px solid #E4ECF5",flexShrink:0}}>
        <button onClick={() => setFilterType("all")}
          style={{padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700,
                  border:`1.5px solid ${filterType==="all"?"#003366":"#DCE3F5"}`,
                  background:filterType==="all"?"#003366":"white",
                  color:filterType==="all"?"white":"#666",cursor:"pointer"}}>All</button>
        {WORKSHEET_TYPES.map(wt => (
          <button key={wt.id} onClick={() => setFilterType(wt.id)}
            style={{padding:"3px 10px",borderRadius:20,fontSize:10,fontWeight:700,
                    border:`1.5px solid ${filterType===wt.id?wt.color:"#DCE3F5"}`,
                    background:filterType===wt.id?wt.bg:"white",
                    color:filterType===wt.id?wt.color:"#666",cursor:"pointer"}}>
            {wt.icon}
          </button>
        ))}
      </div>

      {/* Scrollable list */}
      <div style={{flex:1,overflowY:"auto",padding:"10px 10px 80px",background:"#F4F7FB"}}>
        {loadErr ? (
          <div style={{textAlign:"center",padding:"32px 12px"}}>
            <div style={{fontSize:24,marginBottom:8}}>⚠️</div>
            <div style={{fontSize:11,fontWeight:700,color:"#C0392B",marginBottom:8,lineHeight:1.5}}>
              {loadErr}
            </div>
            <button onClick={load}
              style={{padding:"6px 14px",background:"#003366",color:"white",
                      border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer"}}>
              Retry
            </button>
          </div>
        ) : loading ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                       padding:40,gap:12,flexDirection:"column"}}>
            <div style={{width:22,height:22,border:"3px solid #DCE3F5",
                         borderTop:"3px solid #003366",borderRadius:"50%",
                         animation:"ws-spin .7s linear infinite"}}/>
            <div style={{fontSize:11,color:"#AAA"}}>Loading…</div>
          </div>
        ) : worksheets.length === 0 ? (
          <div style={{textAlign:"center",padding:"40px 12px",color:"#B0BECC"}}>
            <div style={{fontSize:36,marginBottom:8}}>📝</div>
            <div style={{fontSize:12,fontWeight:700,color:"#8FA5BC",marginBottom:4}}>
              {search || filterType !== "all" ? "No matches" : "Nothing saved yet"}
            </div>
            <div style={{fontSize:11,lineHeight:1.7}}>
              {search || filterType !== "all"
                ? "Clear filters to see all"
                : "Save a worksheet to see it here"}
            </div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {worksheets.map(ws => {
              const wt = WORKSHEET_TYPES.find(w => w.id === ws.type) || WORKSHEET_TYPES[0];
              const itemCount = (ws.items || []).length;
              const previews  = (ws.items || []).slice(0, 4);

              return (
                <div key={ws._id}
                  style={{background:"white",borderRadius:12,overflow:"hidden",
                          border:`1.5px solid ${wt.color}30`,
                          boxShadow:"0 1px 6px rgba(0,0,0,.06)",
                          transition:"box-shadow .15s,transform .15s"}}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.1)"; e.currentTarget.style.transform="translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow="0 1px 6px rgba(0,0,0,.06)"; e.currentTarget.style.transform=""; }}>

                  {/* Top color stripe */}
                  <div style={{height:4,background:wt.color}}/>

                  {/* Image strip + title row */}
                  <div style={{display:"flex",gap:0}}>
                    {/* 2×2 thumbnail grid */}
                    <div style={{width:80,flexShrink:0,background:wt.bg,
                                 display:"grid",gridTemplateColumns:"1fr 1fr",
                                 borderRight:`1px solid ${wt.color}20`}}>
                      {previews.length > 0 ? previews.map((item, i) => {
                        const url = resolveUrl(item);
                        return (
                          <div key={i} style={{display:"flex",alignItems:"center",
                                               justifyContent:"center",padding:4,
                                               borderRight:i%2===0?"1px solid rgba(0,0,0,.05)":"none",
                                               borderBottom:i<2&&previews.length>2?"1px solid rgba(0,0,0,.05)":"none",
                                               minHeight:38}}>
                            {url
                              ? <img src={url} alt="" style={{maxWidth:28,maxHeight:28,objectFit:"contain"}}/>
                              : <div style={{fontSize:14,opacity:.3}}>{wt.icon}</div>
                            }
                          </div>
                        );
                      }) : (
                        <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",
                                     justifyContent:"center",padding:8,fontSize:22,opacity:.25}}>
                          {wt.icon}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{flex:1,padding:"10px 10px 8px",minWidth:0}}>
                      <div style={{display:"flex",alignItems:"flex-start",
                                   justifyContent:"space-between",gap:6,marginBottom:4}}>
                        <div style={{fontWeight:800,fontSize:12,color:"#1A2E4A",
                                     overflow:"hidden",textOverflow:"ellipsis",
                                     whiteSpace:"nowrap",flex:1}}>
                          {ws.title || wt.label}
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteWs(ws._id, e); }}
                          style={{background:"#FFF0F0",color:"#C0392B",
                                  border:"1px solid #F5C6C6",borderRadius:6,
                                  padding:"2px 7px",fontSize:10,cursor:"pointer",
                                  fontWeight:700,flexShrink:0,lineHeight:1.4}}>
                          {deleting === ws._id ? "…" : "🗑"}
                        </button>
                      </div>

                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:5}}>
                        <span style={{fontSize:9,fontWeight:700,color:wt.color,
                                      background:wt.bg,padding:"1px 6px",borderRadius:6}}>
                          {wt.icon} {wt.label}
                        </span>
                        <span style={{fontSize:9,fontWeight:600,color:"#8FA5BC",
                                      background:"#F4F7FB",padding:"1px 6px",borderRadius:6}}>
                          {itemCount} img{itemCount !== 1 ? "s" : ""}
                        </span>
                        {ws.language && (
                          <span style={{fontSize:9,fontWeight:600,color:"#8FA5BC",
                                        background:"#F4F7FB",padding:"1px 6px",borderRadius:6}}>
                            {LANGUAGES.find(l=>l.code===ws.language)?.name || ws.language}
                          </span>
                        )}
                        {ws.indicators?.length > 0 && (
                          <span style={{fontSize:9,fontWeight:700,color:"#003366",
                                        background:"#EBF4FB",padding:"1px 6px",borderRadius:6}}>
                            🎯 {ws.indicators.length}
                          </span>
                        )}
                      </div>

                      <div style={{fontSize:10,color:"#C0CEDC",marginBottom:7}}>
                        {relDate(ws.createdAt)}
                        {ws.usedCount > 0 && ` · Printed ${ws.usedCount}×`}
                      </div>

                      <div style={{display:"flex",gap:6}}>
                        <button onClick={() => onLoad(ws)}
                          style={{flex:1,padding:"6px 0",background:wt.color,color:"white",
                                  border:"none",borderRadius:7,fontSize:11,fontWeight:700,
                                  cursor:"pointer"}}>
                          ✏️ Load
                        </button>
                        {onPrint && (
                          <button onClick={() => onPrint(ws)}
                            style={{padding:"6px 10px",background:"#1B6B45",color:"white",
                                    border:"none",borderRadius:7,fontSize:11,fontWeight:700,
                                    cursor:"pointer"}}>
                            🖨️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function WorksheetCreator() {
  const { getAuthHeader } = useAuth();

  const [step,              setStep]              = useState("type");
  const [activeType,        setActiveType]        = useState("odd-one-out");
  const [language,          setLanguage]          = useState("en");
  const [worksheetTitle,    setWorksheetTitle]    = useState("");
  const [customInstruction, setCustomInstruction] = useState("");
  const [selectedItems,     setSelectedItems]     = useState([]);
  const [showPicker,        setShowPicker]        = useState(false);
  const [saving,            setSaving]            = useState(false);
  const [saveMsg,           setSaveMsg]           = useState("");
  const [savedId,           setSavedId]           = useState(null);
  // Bumped after every successful save so the inline panel refreshes
  const [savedRefreshKey,   setSavedRefreshKey]   = useState(0);
  const fileInputRef = useRef();

  // Shared NCF development taxonomy (40 indicators / 5 domains) — same source
  // as CollectionManager / FlashcardGenerator. Used to tag this worksheet (and
  // its items) so SM-2/BKT engagement can later roll up to NCF indicator progress.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/collection/taxonomy`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setTaxonomy(data.taxonomy);
      } catch {}
    })();
  }, []);

  // Worksheet-level NCF indicator tags — the primary tag for this worksheet
  // as a whole. Individual items can also carry their own tags (inherited
  // from Collection when picked).
  const [worksheetIndicators, setWorksheetIndicators] = useState([]);
  const toggleWorksheetIndicator = (id) => {
    setWorksheetIndicators(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const wtype       = WORKSHEET_TYPES.find(t => t.id === activeType);
  const instruction = customInstruction || INSTRUCTIONS[activeType]?.[language] || "";

  // Domains touched by this worksheet right now (worksheet tags ∪ every item's
  // tags) — purely a live preview; the server derives & stores the
  // authoritative `domains` field on save.
  const touchedDomains = (() => {
    if (!taxonomy) return [];
    const all = new Set(worksheetIndicators);
    selectedItems.forEach(it => (it.indicators || []).forEach(id => all.add(id)));
    const domainIds = new Set();
    taxonomy.indicators.forEach(ind => { if (all.has(ind.id)) domainIds.add(ind.domainId); });
    return taxonomy.domains.filter(d => domainIds.has(d.id));
  })();

  const handleFileUpload = useCallback((files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = e => {
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
        setSelectedItems(p => [...p, {
          name, imageUrl: e.target.result, color:"#E8F0FE",
          labels:{en:name}, indicators:[], id:`up-${Date.now()}-${Math.random()}`,
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const toggleItem = item => {
    setSelectedItems(p =>
      p.find(i => i.id === item.id)
        ? p.filter(i => i.id !== item.id)
        : [...p, item]
    );
  };

  const handlePickFromCollection = picked => {
    const newItems = picked.map(p => ({ ...p, labels: p.labels || {en:p.name}, indicators: p.indicators || [] }));
    setSelectedItems(prev => {
      const ex = new Set(prev.map(i => i.id || i.name));
      return [...prev, ...newItems.filter(i => !ex.has(i.id))];
    });
    setShowPicker(false);
  };

  const handleLoadSaved = async (ws) => {
    try {
      const res  = await fetch(`${API}/worksheet/${ws._id}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        const full = data.worksheet;
        const base = API.replace("/api", "");
        setActiveType(full.type);
        setWorksheetTitle(full.title);
        setLanguage(full.language || "en");
        setCustomInstruction(full.instruction || "");
        setSelectedItems((full.items || []).map(it => ({
          ...it,
          imageUrl: it.imageUrl?.startsWith("/uploads") ? `${base}${it.imageUrl}` : it.imageUrl,
          id: it._id || it.collectionId || String(Math.random()),
          indicators: it.indicators || [],
        })));
        setWorksheetIndicators(full.indicators || []);
        setSavedId(full._id);
        setStep("content");
      }
    } catch {}
  };

  // Quick-print directly from saved panel without loading into editor
  const handleQuickPrint = async (ws) => {
    try {
      const res  = await fetch(`${API}/worksheet/${ws._id}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        const full = data.worksheet;
        const base = API.replace("/api", "");
        const items = (full.items || []).map(it => ({
          ...it,
          imageUrl: it.imageUrl?.startsWith("/uploads") ? `${base}${it.imageUrl}` : it.imageUrl,
        }));
        const instr = full.instruction || INSTRUCTIONS[full.type]?.[full.language || "en"] || "";
        await buildAndPrint(full.type, items, instr, full.language || "en", full.title);
        try { await fetch(`${API}/worksheet/${ws._id}/use`, { method:"POST" }); } catch {}
      }
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg("");
    try {
      const base = API.replace("/api", "");
      const body = {
        title:       worksheetTitle || wtype.label,
        type:        activeType,
        language,
        instruction: customInstruction,
        indicators:  worksheetIndicators,
        items: selectedItems.map(it => ({
          collectionId:    (!it.id || it.id.startsWith("up-")) ? null : it.id,
          name:            it.name,
          imageUrl:        it.imageUrl?.startsWith(base) ? it.imageUrl.replace(base, "") : it.imageUrl,
          color:           it.color || "#E8F0FE",
          labels:          it.labels || {en:it.name},
          count:           it.count,
          sequenceIndex:   it.sequenceIndex,
          imageUrlB:       it.imageUrlB,
          differenceCount: it.differenceCount,
          indicators:      it.indicators || [],
        })),
      };
      const method = savedId ? "PUT"  : "POST";
      const url    = savedId ? `${API}/worksheet/${savedId}` : `${API}/worksheet`;
      const res    = await fetch(url, { method, headers: getAuthHeader(), body: JSON.stringify(body) });
      const data   = await res.json();
      if (data.success) {
        setSavedId(data.worksheet._id);
        setSaveMsg("✅ Saved!");
        setSavedRefreshKey(k => k + 1); // refresh inline panel
      }
      else setSaveMsg(`❌ ${data.message || "Save failed"}`);
    } catch {
      setSaveMsg("❌ Backend not reachable");
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(""), 5000);
  };

  const handleGenerate = async () => {
    await buildAndPrint(activeType, selectedItems, instruction, language, worksheetTitle || wtype.label);
    if (savedId) { try { await fetch(`${API}/worksheet/${savedId}/use`, { method:"POST" }); } catch {} }
  };

  const steps      = ["type","content","configure","preview"];
  const stepLabels = ["1. Type","2. Images","3. Configure","4. Preview"];
  const stepIdx    = steps.indexOf(step);

  const EmptyImageState = () => (
    <div style={{textAlign:"center",padding:"40px 20px",background:"white",
                 borderRadius:12,border:"2px dashed #DCE3F5",color:"#AAA"}}>
      <div style={{fontSize:36,marginBottom:8}}>🖼️</div>
      <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>No images selected</div>
      <div style={{fontSize:11}}>Browse Collection Manager or upload images above</div>
    </div>
  );

  return (
    <div style={{fontFamily:"'Segoe UI',Arial,sans-serif",background:"#F7F8FC",
                 minHeight:"100vh",color:"#1a1a2e",display:"flex",flexDirection:"column"}}>

      {/* ── TOP BAR ── */}
      <div style={{background:"white",borderBottom:"1px solid #DCE3F5",
                   padding:"0 20px",display:"flex",alignItems:"center",flexShrink:0}}>
        {steps.map((s,i) => (
          <button key={s} onClick={() => setStep(s)} style={{
            background:"none",border:"none",padding:"14px 14px",fontSize:12,fontWeight:700,
            color:step===s ? wtype.color : "#888",
            borderBottom:`3px solid ${step===s ? wtype.color : "transparent"}`,
            cursor:"pointer",whiteSpace:"nowrap",
          }}>{stepLabels[i]}</button>
        ))}
        <div style={{flex:1}}/>
        {saveMsg && (
          <span style={{fontSize:12,fontWeight:700,marginRight:12,
                        color:saveMsg.startsWith("✅") ? "#1B8A5A" : "#C0392B"}}>
            {saveMsg}
          </span>
        )}
        <button onClick={handleSave} disabled={saving}
          style={{padding:"8px 18px",background:saving?"#CCC":"#003366",color:"white",
                  border:"none",borderRadius:8,fontSize:12,fontWeight:700,
                  cursor:saving?"default":"pointer"}}>
          {saving ? "Saving…" : "💾 Save"}
        </button>
      </div>

      {/* ── TWO-COLUMN BODY ── */}
      <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr 300px",
                   gap:0,overflow:"hidden",minHeight:0}}>

        {/* LEFT: step editor — scrollable */}
        <div style={{overflowY:"auto",padding:"20px 20px 80px"}}>

          {/* ── STEP 1: TYPE ── */}
          {step === "type" && (
            <div>
              <div style={{fontWeight:800,fontSize:18,color:"#003366",marginBottom:6}}>
                Choose Worksheet Type
              </div>
              <div style={{color:"#666",fontSize:13,marginBottom:20}}>
                Select the kind of worksheet you want to create.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14}}>
                {WORKSHEET_TYPES.map(wt => (
                  <div key={wt.id} onClick={() => { setActiveType(wt.id); setStep("content"); }}
                    style={{border:`2.5px solid ${activeType===wt.id?wt.color:"#E0E7F0"}`,
                            borderRadius:14,padding:20,cursor:"pointer",background:"white",
                            boxShadow:activeType===wt.id?`0 4px 20px ${wt.color}33`:"0 1px 4px rgba(0,0,0,.06)",
                            transition:"all .2s"}}>
                    <div style={{fontSize:32,marginBottom:8}}>{wt.icon}</div>
                    <div style={{fontWeight:800,fontSize:14,color:wt.color,marginBottom:5}}>{wt.label}</div>
                    <div style={{fontSize:12,color:"#666",lineHeight:1.5}}>{wt.desc}</div>
                    <div style={{marginTop:12,display:"flex",justifyContent:"flex-end"}}>
                      <div style={{background:wt.bg,color:wt.color,fontSize:11,fontWeight:700,
                                   padding:"3px 10px",borderRadius:10}}>
                        {activeType===wt.id?"✓ Selected":"Select →"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 2: IMAGES ── */}
          {step === "content" && (
            <div>
              <div style={{fontWeight:800,fontSize:17,color:wtype.color,marginBottom:4}}>
                {wtype.icon} {wtype.label}
              </div>
              <div style={{color:"#666",fontSize:13,marginBottom:16}}>
                Select images from Collection Manager or upload your own.
              </div>
              <div style={{display:"flex",gap:12,marginBottom:20}}>
                <button onClick={() => setShowPicker(true)}
                  style={{flex:1,padding:"12px",background:"white",border:"2px solid #003366",
                          borderRadius:10,fontWeight:700,fontSize:13,color:"#003366",cursor:"pointer"}}>
                  🗂️ Browse Collection Manager
                </button>
                <div onClick={() => fileInputRef.current?.click()}
                  style={{flex:1,border:"2px dashed #AABBD6",borderRadius:10,padding:"12px",
                          cursor:"pointer",background:"white",display:"flex",
                          alignItems:"center",justifyContent:"center",gap:8,
                          fontWeight:700,color:"#555",fontSize:13}}>
                  📁 Upload Images
                  <span style={{fontSize:11,color:"#AAA",fontWeight:400}}>PNG · JPG</span>
                  <input ref={fileInputRef} type="file" multiple accept="image/*"
                    style={{display:"none"}} onChange={e => handleFileUpload(e.target.files)}/>
                </div>
              </div>

              {selectedItems.length === 0 ? <EmptyImageState/> : (
                <div style={{marginBottom:20}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#003366"}}>
                      ✅ Selected ({selectedItems.length})
                    </div>
                    <button onClick={() => setSelectedItems([])}
                      style={{padding:"3px 10px",background:"#FFF0F0",color:"#C0392B",
                              border:"1px solid #F5C6C6",borderRadius:6,fontSize:11,cursor:"pointer"}}>
                      Clear all
                    </button>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {selectedItems.map((item,i) => (
                      <div key={i} onClick={() => toggleItem(item)}
                        style={{border:`2px solid ${wtype.color}`,borderRadius:10,padding:8,
                                background:wtype.bg,textAlign:"center",width:90,
                                position:"relative",cursor:"pointer"}}>
                        <div style={{position:"absolute",top:4,right:4,background:wtype.color,
                                     color:"white",borderRadius:"50%",width:16,height:16,
                                     fontSize:9,display:"flex",alignItems:"center",
                                     justifyContent:"center",fontWeight:900}}>✓</div>
                        {item.indicators?.length > 0 && (
                          <div style={{position:"absolute",top:4,left:4,background:"rgba(0,51,102,.85)",
                                       color:"white",fontSize:8,fontWeight:800,padding:"1px 4px",
                                       borderRadius:5}}
                            title={item.indicators.join(", ")}>
                            🎯{item.indicators.length}
                          </div>
                        )}
                        {resolveUrl(item)
                          ? <img src={resolveUrl(item)} style={{width:56,height:56,objectFit:"contain"}} alt=""/>
                          : <div style={{width:56,height:56,background:"#E0E7F0",borderRadius:6,
                                         margin:"0 auto",display:"flex",alignItems:"center",
                                         justifyContent:"center",fontSize:10,color:"#AAA"}}>?</div>
                        }
                        <div style={{fontSize:9,fontWeight:600,color:"#333",marginTop:3,
                                     overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {item.labels?.en||item.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <button onClick={() => setStep("configure")}
                  style={{background:wtype.color,color:"white",border:"none",borderRadius:8,
                          padding:"10px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: CONFIGURE ── */}
          {step === "configure" && (
            <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:20}}>
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#003366",marginBottom:10}}>📋 TITLE</div>
                  <input value={worksheetTitle} onChange={e => setWorksheetTitle(e.target.value)}
                    placeholder={wtype.label}
                    style={{width:"100%",border:"1.5px solid #DCE3F5",borderRadius:8,
                            padding:"8px 10px",fontSize:12,outline:"none",fontFamily:"inherit",
                            boxSizing:"border-box"}}/>
                </div>
                <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#003366",marginBottom:10}}>🌐 LANGUAGE</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {LANGUAGES.map(lang => (
                      <button key={lang.code} onClick={() => setLanguage(lang.code)}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                                padding:"8px 10px",
                                border:language===lang.code?`2px solid ${wtype.color}`:"1.5px solid #DCE3F5",
                                borderRadius:8,background:language===lang.code?wtype.bg:"white",
                                cursor:"pointer",fontSize:12,
                                color:language===lang.code?wtype.color:"#555",fontWeight:600}}>
                        {lang.name}{language===lang.code&&" ✓"}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#003366",marginBottom:4}}>✏️ INSTRUCTION</div>
                  <div style={{fontSize:10,color:"#888",marginBottom:8}}>Leave blank to use default</div>
                  <textarea value={customInstruction} onChange={e => setCustomInstruction(e.target.value)}
                    placeholder={INSTRUCTIONS[activeType]?.[language]} rows={3}
                    style={{width:"100%",border:"1.5px solid #DCE3F5",borderRadius:8,
                            padding:"8px 10px",fontSize:11,outline:"none",
                            fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}/>
                </div>

                <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#003366",marginBottom:4}}>🎯 DEVELOPMENT INDICATORS (NCF)</div>
                  <div style={{fontSize:10,color:"#888",marginBottom:8,lineHeight:1.6}}>
                    What does this worksheet, as a whole, help practice? Items picked from
                    Collection can carry their own tags too.
                  </div>
                  <IndicatorTagPicker taxonomy={taxonomy} selected={worksheetIndicators} onToggle={toggleWorksheetIndicator} compact/>
                  {worksheetIndicators.length > 0 && (
                    <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:8}}>
                      {worksheetIndicators.map(id => {
                        const ind = taxonomy?.indicators.find(i => i.id === id);
                        return (
                          <span key={id} style={{background:"#EBF4FB",color:"#003366",fontSize:10,
                                                  fontWeight:700,padding:"2px 8px",borderRadius:10}}>
                            {id}{ind ? ` · ${ind.label}` : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {touchedDomains.length > 0 && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #f0f4ff"}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#8fa5bc",textTransform:"uppercase",
                                    letterSpacing:.6,marginBottom:6}}>Domains touched</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                        {touchedDomains.map(d => (
                          <span key={d.id} title={d.label}
                            style={{fontSize:11,fontWeight:700,color:"#003366",background:"#EBF4FB",
                                    padding:"2px 8px",borderRadius:8}}>
                            {d.icon} {d.short}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button onClick={() => setStep("preview")}
                  style={{padding:"12px",background:wtype.color,color:"white",border:"none",
                          borderRadius:10,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  Preview & Print →
                </button>
              </div>

              {/* Live preview */}
              <div style={{background:"white",borderRadius:12,padding:18,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:13,color:"#003366",marginBottom:14}}>👁️ Preview</div>
                {selectedItems.length === 0 ? (
                  <div style={{textAlign:"center",padding:40,color:"#AAA",
                               border:"2px dashed #DCE3F5",borderRadius:10}}>
                    <div style={{fontSize:28,marginBottom:8}}>🖼️</div>
                    <div style={{fontSize:12}}>Add images in the Images step to see a preview</div>
                  </div>
                ) : (
                  <div style={{border:`2px solid ${wtype.color}`,borderRadius:10,overflow:"hidden"}}>
                    <div style={{background:wtype.color,color:"white",padding:"8px 14px",fontWeight:700,fontSize:13}}>
                      {worksheetTitle||wtype.label}
                    </div>
                    <div style={{padding:14}}>
                      <div style={{background:wtype.bg,borderLeft:`4px solid ${wtype.color}`,
                                   padding:"7px 10px",borderRadius:"0 6px 6px 0",fontSize:11,marginBottom:14}}>
                        {instruction}
                      </div>
                      {activeType==="odd-one-out"&&(<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>{selectedItems.slice(0,4).map((item,i)=>(<div key={i} style={{border:`1.5px solid ${wtype.color}`,borderRadius:8,padding:8,textAlign:"center"}}>{resolveUrl(item)?<img src={resolveUrl(item)} style={{width:50,height:50,objectFit:"contain"}} alt=""/>:<div style={{width:50,height:50,background:"#F0F4F8",borderRadius:6,margin:"0 auto"}}/>}<div style={{fontSize:9,marginTop:4}}>{item.labels?.en||item.name}</div></div>))}</div>)}
                      {activeType==="match-following"&&(<div style={{display:"flex",flexDirection:"column",gap:8}}>{selectedItems.slice(0,3).map((item,i)=>(<div key={i} style={{display:"flex",alignItems:"center",gap:12}}><div style={{border:`1.5px solid ${wtype.color}`,borderRadius:6,padding:5,width:64,textAlign:"center"}}>{resolveUrl(item)?<img src={resolveUrl(item)} style={{width:36,height:36,objectFit:"contain"}} alt=""/>:<div style={{width:36,height:36,background:"#F0F4F8",borderRadius:4,margin:"0 auto"}}/>}</div><div style={{flex:1,borderTop:"1.5px dashed #CCC"}}/><div style={{border:"1.5px solid #DDD",borderRadius:6,padding:5,width:64,textAlign:"center"}}>{resolveUrl(selectedItems[i+4]||selectedItems[i])?<img src={resolveUrl(selectedItems[i+4]||selectedItems[i])} style={{width:36,height:36,objectFit:"contain"}} alt=""/>:<div style={{width:36,height:36,background:"#F0F4F8",borderRadius:4,margin:"0 auto"}}/>}</div></div>))}</div>)}
                      {activeType==="sequence-order"&&(<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>{selectedItems.slice(0,4).map((item,i)=>(<div key={i} style={{border:`1.5px solid ${wtype.color}`,borderRadius:8,padding:8,textAlign:"center"}}>{resolveUrl(item)?<img src={resolveUrl(item)} style={{width:48,height:48,objectFit:"contain"}} alt=""/>:<div style={{width:48,height:48,background:"#F0F4F8",borderRadius:6,margin:"0 auto"}}/>}<div style={{marginTop:4,border:`1px solid ${wtype.color}`,borderRadius:4,height:16}}/></div>))}</div>)}
                      {activeType==="count-items"&&(<div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>{selectedItems.slice(0,3).map((item,gi)=>(<div key={gi} style={{border:`1.5px solid ${wtype.color}`,borderRadius:8,padding:8}}><div style={{display:"flex",flexWrap:"wrap"}}>{Array.from({length:item.count||gi+2}).map((_,k)=>resolveUrl(item)?<img key={k} src={resolveUrl(item)} style={{width:22,height:22}} alt=""/>:<div key={k} style={{width:22,height:22,background:"#E0E7F0",borderRadius:3,margin:1}}/>)}</div><div style={{marginTop:4,fontSize:9,color:"#888"}}>Count: ___</div></div>))}</div>)}
                      {activeType==="find-differences"&&(<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>{["Picture A","Picture B"].map((label,li)=>(<div key={li} style={{border:`1.5px solid ${wtype.color}`,borderRadius:8,overflow:"hidden"}}><div style={{background:wtype.color,color:"white",fontSize:9,fontWeight:700,padding:"3px 8px"}}>{label}</div><div style={{padding:8,textAlign:"center"}}>{resolveUrl(selectedItems[0])?<img src={resolveUrl(selectedItems[0])} style={{width:72,height:72,objectFit:"contain"}} alt=""/>:<div style={{width:72,height:72,background:"#F0F4F8",borderRadius:6,margin:"0 auto"}}/>}</div></div>))}</div>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 4: PREVIEW & PRINT ── */}
          {step === "preview" && (
            <div style={{maxWidth:560,margin:"0 auto"}}>
              <div style={{background:"white",borderRadius:14,padding:24,
                           boxShadow:"0 2px 12px rgba(0,0,0,.08)",marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
                  <div style={{background:wtype.bg,borderRadius:12,width:50,height:50,
                               display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>
                    {wtype.icon}
                  </div>
                  <div>
                    <div style={{fontWeight:800,fontSize:17,color:wtype.color}}>
                      {worksheetTitle||wtype.label}
                    </div>
                    <div style={{fontSize:12,color:"#888"}}>{wtype.desc}</div>
                  </div>
                </div>
                {[
                  ["Type",          wtype.label],
                  ["Language",      LANGUAGES.find(l=>l.code===language)?.name],
                  ["Images",        `${selectedItems.length} selected`],
                  ["Page Size",     "A4 Portrait (full-bleed)"],
                  ["Student Fields","Name · Date · Score"],
                ].map(([k,v]) => (
                  <div key={k} style={{display:"flex",padding:"7px 0",
                                       borderBottom:"1px solid #F0F4FF",fontSize:13}}>
                    <span style={{color:"#888",minWidth:140}}>{k}:</span>
                    <span style={{fontWeight:600}}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{background:wtype.bg,border:`1.5px solid ${wtype.color}30`,
                           borderRadius:10,padding:14,marginBottom:16,fontSize:12}}>
                <div style={{fontWeight:700,color:wtype.color,marginBottom:4}}>📌 Instruction:</div>
                <div style={{color:"#444"}}>{instruction}</div>
              </div>
              {selectedItems.length === 0 && (
                <div style={{background:"#FFF3F0",border:"1.5px solid #FFBCAA",borderRadius:10,
                             padding:"12px 16px",marginBottom:16,fontSize:12,color:"#C0392B",fontWeight:600}}>
                  ⚠️ No images selected. Go back to the Images step to add some.
                </div>
              )}
              <button onClick={handleGenerate} disabled={selectedItems.length===0}
                style={{width:"100%",padding:16,
                        background:selectedItems.length===0?"#CCC":wtype.color,
                        color:"white",border:"none",borderRadius:12,fontSize:15,fontWeight:800,
                        cursor:selectedItems.length===0?"not-allowed":"pointer",
                        boxShadow:selectedItems.length===0?"none":`0 4px 20px ${wtype.color}44`}}>
                🖨️ Generate &amp; Print Worksheet
              </button>
              <div style={{textAlign:"center",fontSize:11,color:"#888",marginTop:8}}>
                Print dialog will open → choose "Save as PDF" · A4 Portrait
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Saved worksheets — always visible, scrolls independently */}
        <div style={{borderLeft:"1px solid #DCE3F5",background:"#F4F7FB",
                     display:"flex",flexDirection:"column",
                     height:"calc(100vh - 49px)",position:"sticky",top:49,overflow:"hidden"}}>
          <SavedWorksheetsInline
            getAuthHeader={getAuthHeader}
            onLoad={handleLoadSaved}
            onPrint={handleQuickPrint}
            refreshKey={savedRefreshKey}
          />
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,background:"white",
                   borderTop:"1px solid #DCE3F5",padding:"8px 20px",
                   display:"flex",alignItems:"center",gap:10,fontSize:12,
                   color:"#666",zIndex:50}}>
        <div style={{background:wtype.bg,color:wtype.color,fontWeight:700,
                     padding:"3px 10px",borderRadius:8,fontSize:12}}>
          {wtype.icon} {wtype.label}
        </div>
        <span style={{color:"#CCC"}}>·</span>
        <span>Lang: <strong>{LANGUAGES.find(l=>l.code===language)?.name}</strong></span>
        <span style={{color:"#CCC"}}>·</span>
        <span><strong>{selectedItems.length}</strong> images</span>
        {savedId && (
          <span style={{background:"#E8F0FE",color:"#003366",padding:"2px 8px",
                        borderRadius:6,fontSize:11,fontWeight:700}}>📌 Saved</span>
        )}
        <div style={{flex:1}}/>
        {stepIdx > 0 && (
          <button onClick={() => setStep(steps[stepIdx-1])}
            style={{background:"white",color:"#666",border:"1px solid #DCE3F5",
                    borderRadius:6,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            ← Back
          </button>
        )}
        {stepIdx < steps.length-1 && (
          <button onClick={() => setStep(steps[stepIdx+1])}
            style={{background:wtype.color,color:"white",border:"none",borderRadius:6,
                    padding:"6px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            Next →
          </button>
        )}
        {step === "preview" && (
          <button onClick={handleGenerate} disabled={selectedItems.length===0}
            style={{background:selectedItems.length===0?"#CCC":wtype.color,color:"white",
                    border:"none",borderRadius:6,padding:"6px 16px",fontWeight:700,
                    fontSize:12,cursor:selectedItems.length===0?"not-allowed":"pointer"}}>
            🖨️ Print
          </button>
        )}
      </div>
      <div style={{height:52}}/>

      {showPicker && (
        <CollectionPickerModal
          getAuthHeader={getAuthHeader}
          onPick={handlePickFromCollection}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}